import type { HmmRegion } from "@eu.mdotm.api/sphere";
import type { ViewModeEnum } from "@eu.mdotm.api/smart-workspaces";
import { useUserValue } from "$root/functional-areas/user";
import type { ReviewEntity } from "$root/pages/ReviewInstruments.const";
import {
	type DependencyList,
	type Dispatch,
	type ReactNode,
	createContext,
	useContext,
	useEffect,
	useReducer,
	useRef,
} from "react";

type FunctionalAreasContextProps = {
	outlook?: {
		firstFilter?: string;
		secondFilter?: string;
		thirdFilter?: string;
	};
	outlookFocus?: {
		firstFilter?: string;
		secondFilter?: string;
		thirdFilter?: string;
	};
	/**
	 * The Market dashboard: the one market-insights screen with nothing to select — it shows the
	 * cross-asset picture whole. That absence is the context, so there is no filter to carry and the
	 * area needs an explicit `open` marker to be distinguishable from the `{}` an unmount clears it
	 * to (see `useFunctionalAreas`).
	 */
	marketDashboard?: { open?: boolean };
	hmm?: {
		region?: HmmRegion;
	};
	/** `proposal`: the page shows the portfolio's pending proposal rather than the portfolio itself. */
	portfolio?: { uuid?: string; name?: string; proposal?: boolean };
	universe?: { uuid?: string; name?: string };
	marketView?: { uuid?: string; name?: string };
	reference?: { uuid?: string; name?: string };
	benchmark?: { uuid?: string; name?: string };
	storyfolio?: { uuid?: string; name?: string };
	report?: { uuid?: string; name?: string };
	/** `view` is the currently open Table/Chart tab — see `WorkspaceHeader`. */
	workspace?: { uuid?: string; name?: string; view?: ViewModeEnum };
	/**
	 * The composition **review** step (see the ReviewInstruments page): the user is resolving
	 * flagged holdings by choosing proxies before the composition can be saved. Carries the
	 * `entity` kind alongside the UUID because the review write tools are keyed on both — see
	 * the review descriptor in the Sphere AI `appContext`.
	 */
	review?: { uuid?: string; name?: string; entity?: ReviewEntity };
	/**
	 * The Portfolio Lab page: a what-if session whose COLUMNS are virtual investments — scratch
	 * copies of a portfolio / proposal / benchmark / reference that the user reshapes without
	 * touching the original.
	 *
	 * `columns` carries each column's own uuid because that, not the source entity's, is what the
	 * lab tools are keyed on — the two are different ids for the same screen row, and confusing
	 * them addresses the saved portfolio instead of the copy. Present-but-empty (`columns: []`)
	 * means the page is open with nothing loaded, which is why it is distinguishable from the area
	 * not being registered at all.
	 */
	portfolioLab?: {
		columns?: Array<{ columnUuid: string; name?: string; sourceType?: string }>;
		/** The historical scenario the lab is currently bound to, if any. */
		scenarioId?: string | null;
		/**
		 * How the composition editor groups its rows: the first level of its Group by, and the
		 * classification picked under it (absent for Ungroup).
		 */
		groupBy?: {
			firstLevel: "ALLOCATION" | "HOLDINGS";
			classificationUuid?: string;
			classificationName?: string;
		};
	};
};

const defaultContextValue: FunctionalAreasContextProps = {
	outlook: {},
	outlookFocus: {},
	marketDashboard: {},
	hmm: {},
	portfolio: {},
	universe: {},
	marketView: {},
	reference: {},
	benchmark: {},
	storyfolio: {},
	report: {},
	workspace: {},
	review: {},
	portfolioLab: {},
};

type FunctionalAreasDispatch = Dispatch<{
	areas: keyof FunctionalAreasContextProps;
	data: FunctionalAreasContextProps[keyof FunctionalAreasContextProps];
}>;

/**
 * The registered areas. Read by the Sphere AI chat, which turns them into the hidden per-turn app
 * context; a new object on every registration, so every reader re-renders with it.
 */
const FunctionalAreasStateContext = createContext<FunctionalAreasContextProps>(defaultContextValue);

/**
 * The reducer's `dispatch`, separate from the state so that registering an area re-renders only the
 * readers of the state. `useFunctionalAreas` — every page component on the platform — subscribes
 * here, and a `useReducer` dispatch keeps one identity for the provider's lifetime, so those pages
 * never re-render from this context. Keep the two contexts apart.
 */
const FunctionalAreasDispatchContext = createContext<FunctionalAreasDispatch>(() => null);

function reducer(
	state: FunctionalAreasContextProps,
	action:
		| {
				areas: keyof FunctionalAreasContextProps;
				data: FunctionalAreasContextProps[keyof FunctionalAreasContextProps];
		  }
		| { reset: true },
): FunctionalAreasContextProps {
	if ("reset" in action) {
		return defaultContextValue;
	}
	return { ...state, [action.areas]: { ...action.data } };
}

function FunctionalAreasProvider({ children }: { children: ReactNode }): React.JSX.Element {
	const [state, dispatch] = useReducer(reducer, defaultContextValue);
	const user = useUserValue();

	// The functional-area state is in-memory and the provider stays mounted across a
	// logout or user switch, so it would otherwise leak into the next session (e.g. into
	// the Sphere AI app context). Reset it whenever the active session identity changes:
	// signing out, and also switching user without signing out (impersonation keeps
	// `signedIn` true but changes the id). Resetting during render (not in an effect)
	// ensures the state is clean before children render, so a landing page that registers
	// its area on mount isn't wiped afterwards.
	const sessionKey = user.signedIn ? user.id : null;
	const prevSessionKey = useRef(sessionKey);
	if (prevSessionKey.current !== sessionKey) {
		prevSessionKey.current = sessionKey;
		dispatch({ reset: true });
	}

	return (
		<FunctionalAreasDispatchContext.Provider value={dispatch}>
			<FunctionalAreasStateContext.Provider value={state}>{children}</FunctionalAreasStateContext.Provider>
		</FunctionalAreasDispatchContext.Provider>
	);
}

function useFunctionalAreas<TKey extends keyof FunctionalAreasContextProps>(
	optsProvider: () => { areas: TKey; data: FunctionalAreasContextProps[TKey] },
	deps: DependencyList,
): void {
	const dispatch = useContext(FunctionalAreasDispatchContext);
	useEffect(() => {
		const opts = optsProvider();
		dispatch(opts);
		// Clear this area when the registering component unmounts (or before it re-registers on a
		// deps change), so a page's context (e.g. the open workspace) doesn't linger after the user
		// navigates away and leak into consumers like the Sphere AI app context. Only the session
		// reset used to clear it, so stale areas stacked up across navigation. The clear-then-set on
		// a deps change is batched within one effect flush, so consumers never observe the gap.
		return () => dispatch({ areas: opts.areas, data: {} });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, ...deps]);
}

export { FunctionalAreasDispatchContext, FunctionalAreasProvider, FunctionalAreasStateContext, useFunctionalAreas };
export type { FunctionalAreasContextProps };
